import { useState, useEffect, useRef } from "react";
import { IAddress } from "../types";

type Place = google.maps.places.PlaceResult;
type AutoCompleteService = google.maps.places.AutocompleteService;
type PlacesService = google.maps.places.PlacesService;
type Prediction = google.maps.places.AutocompletePrediction;
const ServiceOk = google.maps.places.PlacesServiceStatus.OK;

export default function useGooglePlaces(search: string, placeId: string | undefined, isNewSearch: boolean) {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<IAddress>({});
  const autocompleteService = useRef<AutoCompleteService | null>(null);
  const placesService = useRef<PlacesService | null>(null);

  useEffect(() => {
    if (!autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
  }, []);

  useEffect(() => {
    if (search && isNewSearch) {
      autocompleteService.current?.getPlacePredictions(
        { input: search, types: ["address"], componentRestrictions: { country: "za" } },
        (predictions, status) => {
          if (status === ServiceOk && predictions) {
            setPredictions(predictions);
          } else {
            setPredictions([]);
          }
        },
      );
    } else {
      setPredictions([]);
    }
  }, [search]);

  const getAddressComponent = (place: Place, type: string): string => {
    if (!place.address_components) {
      return "";
    }
    const component = place.address_components.find((c) => c.types.includes(type));
    return component ? component.long_name : "";
  };

  const mapPlaceToIAddress = (place: Place): IAddress => {
    return {
      streetAddress: `${getAddressComponent(place, "street_number")} ${getAddressComponent(place, "route")}`,
      suburb: getAddressComponent(place, "sublocality_level_1") || getAddressComponent(place, "sublocality_level_2"),
      province: getAddressComponent(place, "administrative_area_level_1"),
      postalCode: getAddressComponent(place, "postal_code"),
      country: getAddressComponent(place, "country"),
      city: getAddressComponent(place, "locality"),
      complexBuilding: "",
    };
  };

  useEffect(() => {
    if (!placesService.current) {
      placesService.current = new google.maps.places.PlacesService(document.createElement("div"));
    }
    if (placeId) {
      placesService.current?.getDetails({ placeId }, (place, status) => {
        if (status === ServiceOk && place) {
          setSelectedPlace(mapPlaceToIAddress(place));
        }
      });
    }
  }, [placeId]);

  return { predictions, selectedPlace };
}
